/** Empty state shown when no document tab is open */

import { useNavigate } from 'react-router-dom'
import { Bot, FileUp, GitBranch, KeyRound, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useAppStore } from '../../stores/app-store'
import { buildCreateDocumentBody, useCreateDocument } from '../../hooks/use-documents'
import { getCreditErrorGuidance } from '../../lib/credit-error-guidance'
import { BrandLogo } from '../shared/brand-logo'
import { getWebFeatureProfile } from '../../lib/feature-flags'

export function WelcomeScreen() {
  const storageEnabled = getWebFeatureProfile().storageSpace
  const { theme, openTab, setActiveTab } = useAppStore()
  const createDocument = useCreateDocument()
  const navigate = useNavigate()

  const handleNewDoc = async () => {
    try {
      const payload = buildCreateDocumentBody({
        title: 'Untitled',
        source: 'the AgentWiki editor welcome screen',
      })
      const doc = await createDocument.mutateAsync(payload)
      const tabId = `tab-${doc.id}`
      openTab({ id: tabId, documentId: doc.id, title: doc.title })
      setActiveTab(tabId)
      navigate(`/doc/${doc.slug}/edit`)
    } catch (err) {
      const guidance = getCreditErrorGuidance(err)
      if (guidance) {
        toast.error(guidance.title, {
          description: guidance.description,
          action: { label: guidance.actionLabel, onClick: () => navigate(guidance.actionHref) },
        })
        return
      }
      console.error('Failed to create document:', err)
    }
  }

  const isDark = theme === 'dark'

  return (
    <div className={`aw-welcome-screen ${isDark ? 'dark' : ''}`}>
      <div className="flex flex-col items-center gap-4">
        <BrandLogo size="lg" showWordmark={false} />
        <div className="text-center">
          <h2>Welcome to AgentWiki</h2>
          <p>Write Manual documents or sync Source-backed knowledge that agents can cite.</p>
        </div>
      </div>

      <div className="aw-welcome-actions">
        <button
          onClick={handleNewDoc}
          disabled={createDocument.isPending}
          className="aw-welcome-action primary"
        >
          <Plus className="h-4 w-4" />
          <span>
            <span className="block">Create Manual document</span>
            <span className="block text-xs font-normal text-white/75">
              Start Private workspace-owned knowledge
            </span>
          </span>
        </button>
        <button onClick={() => navigate('/docs/cli')} className="aw-welcome-action">
          <GitBranch className="h-4 w-4" />
          <span>
            <span className="block">Sync repository or vault</span>
            <span className="block text-xs font-normal">Create Source-backed knowledge</span>
          </span>
        </button>
        {storageEnabled && (
          <button onClick={() => navigate('/storage')} className="aw-welcome-action">
            <FileUp className="h-4 w-4" />
            <span>
              <span className="block">Upload file</span>
              <span className="block text-xs font-normal">Extract a source for search</span>
            </span>
          </button>
        )}
        <button onClick={() => navigate('/agents')} className="aw-welcome-action">
          <KeyRound className="h-4 w-4" />
          <span>
            <span className="block">Create agent key</span>
            <span className="block text-xs font-normal">Grant scoped API access</span>
          </span>
        </button>
        <button onClick={() => navigate('/agents')} className="aw-welcome-action dashed">
          <Bot className="h-4 w-4" />
          <span>
            <span className="block">Connect MCP</span>
            <span className="block text-xs font-normal">Open agent setup guide</span>
          </span>
        </button>
      </div>
    </div>
  )
}
